import { baseApi } from './baseApi';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  /** Gesperrt durch die Verwaltung — unabhängig von „aktiv". */
  isLocked?: boolean;
  roles: UserRoleAssignment[];
  mfaEnabled: boolean;
  /** Der Dienst nennt es lastLoginAt; hier stand lastLogin, also blieb die Spalte leer. */
  lastLoginAt?: string | null;
  phone?: string | null;
  language?: string | null;
  timezone?: string | null;
  /** Befristung (Gastrevision) — Tag, JJJJ-MM-TT. */
  validFrom?: string | null;
  validUntil?: string | null;
  ndaAccepted?: boolean;
  ndaAcceptedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Eine Rolle, wie der Dienst sie an der Person führt.
 *
 * Hier stand `roleId`/`roleName` — eine Form, die kein Dienst schickt. Die
 * Folge sah man an drei Stellen: leere Merkzettel im Profil, eine leere Spalte
 * „Rollen" in der Benutzerverwaltung und „0 Benutzer" an jeder Rolle. Nichts
 * davon meldete einen Fehler; alles sah nur so aus, als wäre nichts vergeben.
 */
export interface UserRoleAssignment {
  id: string;
  name: string;
  displayName?: string | null;
}

/** Der Name, der einem Menschen etwas sagt — sonst der technische. */
export const rollenName = (rolle: UserRoleAssignment): string =>
  rolle.displayName?.trim() || rolle.name;

/**
 * Was die Maske der Benutzerverwaltung sendet.
 *
 * Telefon, Sprache, Zeitzone und die Felder der Gastrevision standen in der
 * Maske, aber nicht hier — sie gingen nie hinaus, und wer sie ausfüllte,
 * fand sie beim nächsten Öffnen leer.
 */
export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roleIds: string[];
  phone?: string;
  language?: string;
  timezone?: string;
  validFrom?: string;
  validUntil?: string;
  ndaAccepted?: boolean;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
  roleIds?: string[];
  phone?: string;
  language?: string;
  timezone?: string;
  /** `null` hebt die Befristung auf. */
  validFrom?: string | null;
  validUntil?: string | null;
  ndaAccepted?: boolean;
}

/** Der eigene Datensatz — mehr Felder als die Liste zeigt. */
export interface SelfUser extends User {
  phone?: string | null;
  language?: string;
  timezone?: string;
}

export interface UpdateSelfRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  language?: string;
  timezone?: string;
}

interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  roleId?: string;
  sort?: string;
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<{ data: User[]; total: number }, UsersQueryParams>({
      query: ({ page = 1, limit = 50, ...rest }) => ({
        url: '/users',
        params: { page, limit, ...rest },
      }),
      providesTags: ['Users'],
    }),
    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<User, { id: string; data: UpdateUserRequest }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Users', id }, 'Users'],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    resetUserPassword: builder.mutation<void, { id: string; password: string }>({
      // Der Dienst kennt keine eigene Adresse dafür; das Kennwort ist ein Feld
      // des Benutzers wie jedes andere. Die Adresse /users/:id/reset-password
      // gab es hier zwar, aber nicht dort — jeder Aufruf wäre auf 404 gelaufen.
      // Der Dienst beendet dabei die laufenden Sitzungen der Person.
      query: ({ id, password }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: { password },
      }),
    }),
    // Sperren beendet die laufenden Sitzungen; Entsperren setzt auch den
    // Zähler der Fehlversuche zurück.
    lockUser: builder.mutation<User, string>({
      query: (id) => ({ url: `/users/${id}/lock`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Users', id }, 'Users'],
    }),
    unlockUser: builder.mutation<User, string>({
      query: (id) => ({ url: `/users/${id}/unlock`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Users', id }, 'Users'],
    }),
    // Der Rechtebestand für die Rezertifizierung, als CSV-Text.
    exportAccessReview: builder.query<string, void>({
      query: () => ({ url: '/users/export', responseHandler: (response) => response.text() }),
      keepUnusedDataFor: 0,
    }),

    // --- Selbstbedienung: die eigenen Daten ---------------------------------

    getMe: builder.query<SelfUser, void>({
      query: () => '/users/me',
      providesTags: ['Users'],
    }),
    updateMe: builder.mutation<SelfUser, UpdateSelfRequest>({
      query: (body) => ({ url: '/users/me', method: 'PUT', body }),
      invalidatesTags: ['Users'],
    }),
    changeOwnPassword: builder.mutation<
      void,
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({ url: '/users/me/password', method: 'POST', body }),
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdateMeMutation,
  useChangeOwnPasswordMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetUserPasswordMutation,
  useLockUserMutation,
  useUnlockUserMutation,
  useLazyExportAccessReviewQuery,
} = usersApi;
